import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock, TrendingUp } from 'lucide-react';

export const SearchModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  // ✅ بارگذاری جستجوهای اخیر از localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (error) {
        console.error('Error loading recent searches:', error);
        setRecentSearches([]);
      }
    }
  }, []);

  // ✅ ذخیره جستجوهای اخیر در localStorage
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length === 0) return;
    
    // حذف تکراری و اضافه کردن به اول لیست
    const updatedSearches = [
      query.trim(),
      ...recentSearches.filter(s => s.toLowerCase() !== query.trim().toLowerCase())
    ].slice(0, 5); // فقط 5 تای اخیر رو نگه دار
    
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  const [allProducts, setAllProducts] = useState([]);
  // ✅ دسته‌بندی‌های پرطرفدار داینامیک از API (هماهنگ با دیتابیس)
  const [popularCategories, setPopularCategories] = useState([]);

  // واکشی دسته‌بندی‌ها هنگام باز شدن مودال
  useEffect(() => {
    if (!isOpen || popularCategories.length > 0) return;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${API_BASE}/categories/`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results ?? [];
        setPopularCategories(list.map(c => ({
          name: c.name,
          slug: c.slug,
          icon: c.icon || '🔶',
        })));
      })
      .catch(() => {});
  }, [isOpen]);

  // واکشی محصولات هنگام باز شدن مودال
  useEffect(() => {
    if (!isOpen || allProducts.length > 0) return;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${API_BASE}/products/`)
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : data.results ?? [];
        setAllProducts(list.map(p => ({
          id: p.id,
          name: p.name,
          category: p.category?.name || '',
          categorySlug: p.category?.slug || '',
          price: Number(p.current_price || 0),
          image: p.main_image || '',
          slug: p.slug,
        })));
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.length > 2) {
        performSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const performSearch = () => {
    setLoading(true);
    
    setTimeout(() => {
      const results = allProducts.filter(product => 
        product.name.includes(searchQuery) || 
        product.category.includes(searchQuery)
      );
      setSearchResults(results);
      setLoading(false);
    }, 400);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  // ✅ هنگام جستجو، ذخیره در recent و هدایت به صفحه محصولات
  const handleSearchSubmit = () => {
    if (searchQuery.length > 2) {
      saveRecentSearch(searchQuery); // ✅ ذخیره جستجو
      onClose();
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  // ✅ کلیک روی جستجوی اخیر
  const handleRecentSearchClick = (search) => {
    setSearchQuery(search);
    saveRecentSearch(search);
    onClose();
    navigate(`/products?search=${encodeURIComponent(search)}`);
  };

  // ✅ کلیک روی دسته‌بندی - هدایت با فیلتر
  const handleCategoryClick = (category) => {
    saveRecentSearch(category.name);
    onClose();
    navigate(`/products?category=${encodeURIComponent(category.name)}`);
  };

  // ✅ کلیک روی محصول در نتایج جستجو
  const handleProductClick = (productId) => {
    saveRecentSearch(searchQuery); // ذخیره جستجو
    onClose();
    navigate(`/product/${productId}`);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-20" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-[calc(100vh-10rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="جستجوی محصولات..."
              className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none text-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearchSubmit();
                }
              }}
              autoFocus
            />
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* جستجوهای اخیر */}
        {searchQuery.length === 0 && recentSearches.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-700 flex items-center">
                <Clock className="w-4 h-4 ml-2" />
                جستجوهای اخیر
              </h3>
              <button 
                onClick={clearRecentSearches}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                پاک کردن همه
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearchClick(search)}
                  className="px-4 py-2 bg-gray-100 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* نتایج جستجو */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-500">در حال جستجو...</p>
          </div>
        ) : searchResults.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            <h3 className="font-semibold text-gray-700 mb-3 sticky top-0 bg-white py-2">
              نتایج جستجو ({searchResults.length})
            </h3>
            {searchResults.map((result) => (
              <button
                key={result.id}
                onClick={() => handleProductClick(result.id)}
                className="w-full flex items-center gap-4 p-3 hover:bg-amber-50 rounded-lg transition-colors border border-transparent hover:border-amber-200 text-right"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-lg flex-shrink-0 flex items-center justify-center">
                  <span className="text-3xl">💎</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold truncate">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.category} • {result.weight} گرم • {result.karat} عیار</p>
                </div>
                <p className="text-amber-600 font-bold whitespace-nowrap">
                  {result.price.toLocaleString()} تومان
                </p>
              </button>
            ))}
          </div>
        ) : searchQuery.length > 2 ? (
          <div className="text-center py-12 text-gray-500">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-semibold mb-2">نتیجه‌ای یافت نشد</p>
            <p className="text-sm">نتیجه‌ای برای "{searchQuery}" یافت نشد</p>
            <button 
              onClick={() => setSearchQuery('')}
              className="mt-4 text-amber-600 hover:text-amber-700"
            >
              پاک کردن جستجو
            </button>
          </div>
        ) : null}

        {/* دسته‌بندی‌های پرطرفدار */}
        <div className="mt-6 pt-6 border-t">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
            <TrendingUp className="w-4 h-4 ml-2" />
            دسته‌بندی‌های پرطرفدار
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {popularCategories.map((category) => (
              <button
                key={category.name}
                onClick={() => handleCategoryClick(category)}
                className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg hover:from-amber-100 hover:to-yellow-100 transition-all hover:shadow-md"
              >
                <span className="text-3xl mb-2 block">{category.icon}</span>
                <span className="text-sm font-semibold">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;










